import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  FC,
  useEffect,
  useRef,
} from 'react'
import { useLocation } from 'react-router-dom'
import { toAbsoluteUrl } from '_metronic/helpers'
import { LayoutSplashScreen } from '_metronic/layout/core'
import { useAuth } from '../../auth'
import {
  getEmpresaSlugFromLoginPath,
  useLoginCompanyConfig,
} from '../../auth/core/loginCompanyConfig'
import { BrandConfig, BrandConfigResponse } from '../core/_models'
import { fetchBrandConfig } from '../core/_requests'
import { saveBrandSplash, useBrandSplash } from '../core/brandSplash'
import { parseHexColor, toRgbString } from '../core/color'

/** Teto para a splash esperar a marca na primeira carga, se a API dela demorar. */
const INITIAL_BRAND_SPLASH_TIMEOUT_MS = 5000
/** Teto absoluto da splash segurada pelo BrandProvider (marca + usuário). */
const SPLASH_HOLD_MAX_MS = 30000

/** Mesmo valor do `<title>` de `public/index.html` */
const DEFAULT_DOCUMENT_TITLE = 'LauduzOne'
/** Título neutro do login de empresa enquanto o nome dela não chega */
const COMPANY_LOGIN_DOCUMENT_TITLE = 'Entrar'

const DEFAULT_FAVICON = '/favicon.ico'
/** Fonte do desenho: `public/media/favicons/neutral.svg` */
const NEUTRAL_FAVICON = '/media/favicons/neutral.ico'

interface BrandContextType {
  currentBrand: BrandConfigResponse | undefined
}

const BrandContext = createContext<BrandContextType | undefined>(undefined)

interface BrandProviderProps {
  children: ReactNode
}

export const BrandProvider: FC<BrandProviderProps> = ({ children }) => {
  const { auth, currentUser } = useAuth()
  // Lê a store (e não `currentBrand`) para o título já vir da cópia salva ao recarregar a página
  const brandSplash = useBrandSplash()
  const location = useLocation()

  const [currentBrand, setCurrentBrand] = useState<BrandConfigResponse | undefined>()
  const [isHoldingSplash, setIsHoldingSplash] = useState(false)
  const [isInitialBrandPending, setIsInitialBrandPending] = useState(false)
  const didInitialBrandLoad = useRef(false)

  const fetchBrandDataFromBackend = useCallback(async () => {
    try {
      const backendBrand = await fetchBrandConfig()

      saveBrandSplash(backendBrand)

      if (!backendBrand) return

      setCurrentBrand(backendBrand)
    } catch (error) {
      // Falha de rede/servidor não é "empresa sem marca": mantém a cópia salva (splash, título)
      // em vez de apagá-la e fazer a próxima abertura voltar ao LauduzOne.
      console.error('Failed to fetch brand config:', error)
    }
  }, [])

  const empresaSlug = getEmpresaSlugFromLoginPath(location.pathname)
  const isCompanyLogin = !!empresaSlug
  // Mesma query (e cache) que o AuthInit pré-carrega e a tela de login usa
  const { data: loginCompanyConfig } = useLoginCompanyConfig(empresaSlug)

  // Título da aba — mesma regra do script no <head> do index.html:
  // - login de empresa: nome da empresa (neutro enquanto a configuração não chega);
  // - empresa white-label de fato (tem logo próprio, mesmo critério da splash): nome fantasia;
  // - demais casos, e após logout: padrão.
  useEffect(() => {
    if (isCompanyLogin) {
      document.title = loginCompanyConfig?.name || COMPANY_LOGIN_DOCUMENT_TITLE
      return
    }

    document.title =
      brandSplash?.logoUrl && brandSplash.fantasia ? brandSplash.fantasia : DEFAULT_DOCUMENT_TITLE
  }, [brandSplash, isCompanyLogin, loginCompanyConfig?.name])

  // Favicon neutro para empresa white-label e no login de empresa (onde a splash também é
  // neutra); favicon LauduzOne nos demais casos. Mesma regra do script no <head> do index.html.
  const useNeutralFavicon = !!brandSplash?.logoUrl || isCompanyLogin
  useEffect(() => {
    const favicon = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
    if (favicon) favicon.href = toAbsoluteUrl(useNeutralFavicon ? NEUTRAL_FAVICON : DEFAULT_FAVICON)
  }, [useNeutralFavicon])

  useEffect(() => {
    if (currentBrand?.layout) injectBrandCSSVariables(currentBrand.layout)
  }, [currentBrand])

  useEffect(() => {
    if (!auth?.accessToken) {
      // Logout ou login falho: o próximo login volta a segurar a splash até a marca
      // (possivelmente de outra empresa) chegar.
      didInitialBrandLoad.current = false
      setIsHoldingSplash(false)
      // Sem isso, o login sem slug (`LogoComponent` sem `src`) exibiria o logo da empresa
      // anterior depois do logout.
      setCurrentBrand(undefined)
      return
    }

    // O accessToken também muda no meio da sessão (certificado, desbloqueio de sessão):
    // a splash só segura a tela na primeira carga, para o header não piscar com a marca padrão.
    if (didInitialBrandLoad.current) {
      fetchBrandDataFromBackend()
      return
    }

    didInitialBrandLoad.current = true
    setIsHoldingSplash(true)
    setIsInitialBrandPending(true)

    fetchBrandDataFromBackend().finally(() => setIsInitialBrandPending(false))
  }, [auth?.accessToken, fetchBrandDataFromBackend])

  // No login, a marca costuma chegar antes do usuário (`reloadUser` busca usuário + avatar):
  // soltar a splash só com a marca reexibe a tela de login até o app montar.
  useEffect(() => {
    if (isHoldingSplash && !isInitialBrandPending && currentUser) setIsHoldingSplash(false)
  }, [isHoldingSplash, isInitialBrandPending, currentUser])

  // API da marca lenta: desiste só da marca (o app abre com a padrão e troca quando ela chegar).
  // A espera pelo usuário não entra nesse teto — soltar a splash antes dele reexibe o login.
  useEffect(() => {
    if (!isInitialBrandPending) return

    const timeout = window.setTimeout(
      () => setIsInitialBrandPending(false),
      INITIAL_BRAND_SPLASH_TIMEOUT_MS
    )
    return () => window.clearTimeout(timeout)
  }, [isInitialBrandPending])

  // Rede de segurança: o `reloadUser` termina carregando o usuário ou fazendo logout (que solta a
  // splash), mas se nenhum dos dois acontecer a splash não pode prender a tela para sempre.
  useEffect(() => {
    if (!isHoldingSplash) return

    const timeout = window.setTimeout(() => setIsHoldingSplash(false), SPLASH_HOLD_MAX_MS)
    return () => window.clearTimeout(timeout)
  }, [isHoldingSplash])

  useEffect(() => {
    const handleBrandRefresh = () => {
      void fetchBrandDataFromBackend()
    }

    window.addEventListener('brandDataRefresh', handleBrandRefresh)

    return () => {
      window.removeEventListener('brandDataRefresh', handleBrandRefresh)
    }
  }, [fetchBrandDataFromBackend])

  const value: BrandContextType = {
    currentBrand,
  }

  return (
    <BrandContext.Provider value={value}>
      {isHoldingSplash && <LayoutSplashScreen />}
      {children}
    </BrandContext.Provider>
  )
}

export const useBrand = (): BrandContextType => {
  const context = useContext(BrandContext)
  if (!context) {
    throw new Error('useBrand must be used within BrandProvider')
  }
  return context
}

export const getBrandCSSVariables = (brand: BrandConfig): Record<string, string> => {
  const primaryRgb = toRgbString(parseHexColor(brand.primaryColor) ?? [0, 0, 0])

  return {
    '--bs-primary': brand.primaryColor,
    '--bs-primary-rgb': primaryRgb,
    '--bs-primary-active': brand.primaryActiveColor,
    '--bs-aside-bg-color': brand.asideColor,
    '--bs-link-color': brand.primaryColor,
    '--brand-primary': brand.primaryColor,
  }
}

export const injectBrandCSSVariables = (brand: BrandConfig): void => {
  const root = document.documentElement
  const variables = getBrandCSSVariables(brand)

  Object.entries(variables).forEach(([key, value]) => {
    root.style.setProperty(key, value)
  })

  const metaTheme = document.querySelector('meta[name="theme-color"]')
  if (metaTheme) metaTheme.setAttribute('content', brand.primaryColor)

  window.dispatchEvent(
    new CustomEvent('brandChanged', {
      detail: { brand, variables },
    })
  )
}
